import { createTheme } from "@mui/material/styles";

/**
 * Identidad visual de Adaltra trasladada al sistema de MUI.
 * Los colores y el aire del portal viven aquí y no en hojas de estilo sueltas:
 * cualquier ajuste de marca se hace en un único sitio.
 *
 * Cada color existe dos veces y conviene saber por qué:
 *
 * - `marcaFija` guarda el valor literal del tema claro. Lo necesitan las
 *   pantallas de acceso —que van siempre sobre el violeta corporativo, den la
 *   hora que den las preferencias del sistema— y `alpha()`, que sabe leer un
 *   hexadecimal pero no una variable CSS.
 * - `brand` guarda ese mismo color a través de una variable. Es el que usan
 *   las pantallas, y es lo que permite que el tema oscuro cambie el portal
 *   entero redefiniendo catorce variables en lugar de tocar cien ficheros.
 */
const CLARO = {
  /** Color corporativo, tomado del propio logotipo. */
  violet: "#463a86",
  /** Violeta profundo para degradados y superficies muy oscuras. */
  violetDeep: "#2a2154",
  teal: "#08a3a4",
  /**
   * El turquesa, rebajado hasta que se pueda leer.
   *
   * El de marca es un color de superficie, no de texto: sobre blanco da
   * 3,10:1 y la norma pide 4,5 para texto pequeño. Se usaba tal cual en los
   * rótulos de estado de «En curso», que además van a 12 px. Este llega a
   * 5,01 sin salirse del tono, y de paso quita la sensación de neón que daba
   * el color de marca a plena saturación sobre fondo claro.
   */
  tealTexto: "#0b7c7d",
  ink: "#172b3d",
  /**
   * Gris de apoyo.
   *
   * Estaba en #637487, que sobre el lienzo (#f5f7f8) da 4,46:1 y se queda a
   * dos centésimas del 4,5 que exige la norma. Este tono lo cruza sin cambiar
   * la sensación de la paleta.
   */
  muted: "#5d6f83",
  canvas: "#f5f7f8",
  line: "#dde4e8",
  green: "#26855c",
  greenBg: "#e8f6ef",
  amber: "#a56512",
  amberBg: "#fff4df",
  red: "#c23b3b",
  redBg: "#fff0ef",
  violetBg: "#efedf8",
  papel: "#ffffff",
  /**
   * El degradado corporativo de las piezas destacadas: la invitación de la
   * portada, el resumen de lo aprobado, el total de un presupuesto y la
   * cabecera de la ayuda.
   *
   * Vive aquí y no escrito cuatro veces porque encima siempre lleva texto
   * blanco, y eso obliga a que el violeta se mantenga oscuro. Con el token de
   * violeta a secas —que de noche se aclara para poder leerse *como texto*—
   * esas cuatro piezas pasaban a blanco sobre #a394f2: 2,6:1, por debajo de
   * la mitad de lo exigible. Este par de violetas da 6,9:1 en el peor punto
   * del degradado, dé la hora que dé el tema.
   */
  degradado: "linear-gradient(135deg, #463a86 0%, #2a2154 100%)",
};

/**
 * El mismo portal de noche.
 *
 * No es la paleta clara invertida: eso da grises sucios y un violeta que se
 * hunde en el fondo. Los neutros se han vuelto a mezclar sobre el azul de
 * `ink`, para que el gris del portal siga siendo el gris de Adaltra y no un
 * gris de sistema; y los colores de marca se han aclarado, porque un #463a86
 * sobre un fondo casi negro no se lee.
 *
 * Todos los tonos de texto pasan el 4,5:1 sobre su superficie: el violeta da
 * 6,6, el gris de apoyo 6,9 y el rojo —el más justo— 5,9.
 */
const OSCURO = {
  violet: "#a394f2",
  violetDeep: "#6d5cc9",
  teal: "#2ad0d1",
  // De noche no hace falta rebajarlo: el turquesa claro sobre el panel
  // oscuro ya da 9,0:1.
  tealTexto: "#2ad0d1",
  ink: "#e7edf3",
  muted: "#97a7b8",
  canvas: "#0e1319",
  line: "#26313d",
  green: "#4ecf94",
  greenBg: "#102a20",
  amber: "#e6a94a",
  amberBg: "#2e2312",
  red: "#f2726f",
  redBg: "#33191a",
  violetBg: "#221d3a",
  papel: "#161d26",
  // Levantado respecto al de día: el mismo violeta sobre un fondo casi negro
  // se hundía en la página y la pieza dejaba de leerse como una pieza.
  degradado: "linear-gradient(135deg, #5b4da3 0%, #342a66 100%)",
};

/** Nombre de la variable CSS de cada color. */
const variable = (clave) => `--marca-${clave.toLowerCase()}`;

/** Los colores literales del tema claro. Ver arriba para cuándo usarlos. */
export const marcaFija = CLARO;

/** Los mismos colores, pero atentos al tema. */
export const brand = Object.fromEntries(
  Object.keys(CLARO).map((clave) => [clave, `var(${variable(clave)})`]),
);

/** Las declaraciones de un esquema, listas para `:root` o para `html.dark`. */
const declarar = (tonos) =>
  Object.fromEntries(
    Object.entries(tonos).map(([clave, valor]) => [variable(clave), valor]),
  );

/**
 * Los dos tonos que no salen de la paleta: el globo de ayuda va siempre al
 * contrario del fondo, y la barra de desplazamiento se aclara al pasar por
 * encima. Viajan con el resto para que un subárbol al que se le fuerce un
 * esquema —el acceso, que va siempre en claro— los reciba también.
 */
const SUELTOS_CLAROS = {
  "--marca-globo": "rgba(23, 43, 61, 0.94)",
  "--marca-globo-texto": "#ffffff",
  "--marca-barra-encima": "#c2ced6",
};
const SUELTOS_OSCUROS = {
  "--marca-globo": "rgba(224, 233, 241, 0.95)",
  "--marca-globo-texto": "#101820",
  "--marca-barra-encima": "#3a4757",
};

export const variablesClaras = { ...declarar(CLARO), ...SUELTOS_CLAROS };
export const variablesOscuras = { ...declarar(OSCURO), ...SUELTOS_OSCUROS };

/**
 * Un color rebajado hasta dejar ver lo que hay detrás.
 *
 * Por el portal había una docena de fondos hechos pegando dos dígitos al
 * final del color —`${brand.violet}14`—, que es un hexadecimal de ocho
 * cifras
 * y funciona mientras el color sea un hexadecimal. En cuanto viaja dentro de
 * una variable CSS el resultado es `var(--marca-violeta)14`, que el navegador
 * descarta sin decir nada: el fondo desaparece y nadie se entera.
 *
 * `color-mix` hace lo mismo sin depender de cómo esté escrito el color, y
 * además el porcentaje se lee.
 */
export const velo = (color, porcentaje) =>
  `color-mix(in srgb, ${color} ${porcentaje}%, transparent)`;

/** Sombra suave y homogénea; sustituye a las 25 elevaciones por defecto. */
const softShadow = "0 4px 18px rgba(16, 47, 76, 0.07)";
const raisedShadow = "0 12px 34px rgba(16, 47, 76, 0.12)";

/**
 * Vocabulario de movimiento del portal.
 *
 * Antes cada pantalla escribía su propia curva a mano y había cuatro casi
 * iguales repartidas por cuatro ficheros. Aquí viven una sola vez, con el
 * criterio detrás de cada una:
 *
 * - `entrada`: algo que aparece o desaparece. Arranca rápido, de modo que la
 *   interfaz parece responder en el mismo instante en que se pulsa. Las
 *   curvas que trae CSS de serie son demasiado flojas para eso.
 * - `recorrido`: algo que ya está en pantalla y se mueve de un sitio a otro.
 *   Ahí sí conviene acelerar y frenar, como se mueve cualquier objeto real.
 * - `cajon`: la curva de los paneles que entran desde un borde, tomada de
 *   Ionic; es la que usan las apps del sistema y por eso se reconoce.
 *
 * Nunca `ease-in` a secas: empieza despacio justo en el momento en que se
 * está mirando, y hace que todo parezca ir con retraso.
 */
export const motion = {
  entrada: "cubic-bezier(0.23, 1, 0.32, 1)",
  recorrido: "cubic-bezier(0.77, 0, 0.175, 1)",
  cajon: "cubic-bezier(0.32, 0.72, 0, 1)",
};

/**
 * Duraciones, en milisegundos.
 *
 * El techo de la interfaz son 300 ms: por encima se percibe como lentitud
 * aunque la curva sea buena. Los paneles a pantalla completa son la única
 * excepción, porque recorren mucha más distancia.
 */
export const timing = {
  /** Realimentación de pulsación. */
  pulsacion: 160,
  /** Avisos y globos pequeños. */
  globo: 160,
  /** Menús y desplegables. */
  menu: 200,
  /** Entrada de un bloque de contenido. */
  panel: 260,
  /** Paneles grandes y diálogos. */
  escena: 320,
  /** Intercambio de los dos paneles del acceso. */
  intercambio: 460,
};

/**
 * Realimentación de pulsación, compartida por todo lo pulsable.
 *
 * Sin esto nada parece estar escuchando: se pulsa y no pasa nada hasta que
 * llega la respuesta del servidor. Un 3 % de reducción es suficiente para que
 * el dedo note que el control ha registrado el toque, y lo bastante discreto
 * como para no leerse como un efecto.
 */
const pressFeedback = {
  transition: `transform ${timing.pulsacion}ms ${motion.entrada}`,
  "&:active": { transform: "scale(0.97)" },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:active": { transform: "none" },
  },
};

/**
 * Alto de la ventana descontando lo que tape el navegador.
 *
 * Se resuelve en `MuiCssBaseline`: `100dvh` donde existe y `100vh` donde no.
 */
export const ALTO_PANTALLA = "var(--alto-pantalla)";

/** Ancho equivalente, para las mismas razones. */
export const ANCHO_PANTALLA = "var(--ancho-pantalla)";

/** Anchos entre los que el contenido escala de forma continua. */
const MIN_VIEWPORT = 360;
const MAX_VIEWPORT = 1440;

/**
 * Tamaño que crece con la ventana en lugar de saltar en cada punto de
 * ruptura: interpola linealmente entre `minPx` y `maxPx` y se detiene fuera
 * del rango, de modo que ni se queda diminuto en un móvil ni se dispara en un
 * monitor grande.
 */
export function fluid(minPx, maxPx) {
  const pendiente = (maxPx - minPx) / (MAX_VIEWPORT - MIN_VIEWPORT);
  const base = (minPx - pendiente * MIN_VIEWPORT).toFixed(2);
  const porVw = (pendiente * 100).toFixed(4);
  return `clamp(${minPx}px, ${base}px + ${porVw}vw, ${maxPx}px)`;
}

/**
 * La paleta de un esquema, armada a partir de sus colores literales.
 *
 * Aquí van valores de verdad y no variables: MUI calcula a partir de cada
 * color su versión clara, su versión oscura y el color de texto que hay que
 * poner encima, y para eso necesita poder leerlo.
 */
const paletaDe = (tonos, extras) => ({
  primary: { main: tonos.violet, ...extras.primary },
  secondary: { main: tonos.teal, ...extras.secondary },
  info: { main: tonos.violet },
  success: { main: tonos.green, light: tonos.greenBg },
  warning: { main: tonos.amber, light: tonos.amberBg },
  error: { main: tonos.red, light: tonos.redBg },
  background: { default: tonos.canvas, paper: tonos.papel },
  text: { primary: tonos.ink, secondary: tonos.muted },
  divider: tonos.line,
});

export const theme = createTheme({
  /**
   * El selector `class` deja el esquema colgado de una clase en `<html>`, y
   * eso es lo que permite el barrido: durante la transición hay un instante
   * en el que conviven la foto de antes y la de después, y con variables CSS
   * el cambio de las dos paletas es un solo repintado.
   */
  cssVariables: { colorSchemeSelector: "class" },
  colorSchemes: {
    light: {
      palette: paletaDe(CLARO, {
        primary: { dark: "#352b6b", contrastText: "#fff" },
        secondary: { contrastText: "#fff" },
      }),
    },
    /**
     * De noche el texto de los botones no puede seguir siendo blanco: el
     * violeta se ha aclarado para poder leerse sobre el fondo, y blanco sobre
     * ese violeta da 2,6:1. El texto oscuro sobre el mismo violeta da 6,7.
     */
    dark: {
      palette: paletaDe(OSCURO, {
        primary: { dark: "#8574e0", contrastText: "#171130" },
        secondary: { contrastText: "#04262b" },
      }),
    },
  },
  shape: { borderRadius: 14 },
  /**
   * Las curvas y los tiempos del portal pasan a ser también los de MUI: así el
   * cajón, los menús, los avisos y los diálogos que trae la librería se mueven
   * con el mismo criterio que lo escrito a mano, en vez de con sus valores de
   * fábrica.
   *
   * Salir es más rápido que entrar. Al abrir algo se acompaña al ojo; al
   * cerrarlo lo que se quiere es que desaparezca de en medio.
   */
  transitions: {
    easing: {
      easeOut: motion.entrada,
      easeInOut: motion.recorrido,
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    },
    duration: {
      shortest: 120,
      shorter: 180,
      short: 220,
      standard: timing.panel,
      complex: timing.escena,
      enteringScreen: 240,
      leavingScreen: 180,
    },
  },
  typography: {
    fontFamily: '"Lexend", "Segoe UI", Arial, sans-serif',
    // Toda la escala es fluida: el texto acompaña al ancho de la ventana en
    // vez de cambiar de golpe al cruzar un punto de ruptura.
    h1: { fontSize: fluid(24, 34), fontWeight: 600, letterSpacing: "-0.02em" },
    h2: { fontSize: fluid(19, 24), fontWeight: 600, letterSpacing: "-0.015em" },
    h3: { fontSize: fluid(16, 19), fontWeight: 600 },
    h4: { fontSize: fluid(15, 17), fontWeight: 600 },
    subtitle1: { fontSize: fluid(14.5, 16), fontWeight: 500 },
    subtitle2: { fontSize: fluid(12.8, 14), fontWeight: 600 },
    body1: { fontSize: fluid(14, 15.5) },
    body2: { fontSize: fluid(13, 14) },
    caption: { fontSize: fluid(11.5, 12.5) },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
    /** Antetítulo en versales usado en cabeceras y tarjetas. */
    overline: {
      fontSize: fluid(10.5, 11.5),
      fontWeight: 700,
      letterSpacing: "0.12em",
      lineHeight: 1.6,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        /**
         * Alto de pantalla real.
         *
         * `100vh` en un móvil incluye la barra de direcciones aunque esté
         * fuera, y eso deja el pie de las pantallas por debajo del borde
         * visible. `100dvh` mide lo que de verdad se ve.
         *
         * Vive en una variable y no escrito en cada pantalla porque la forma
         * habitual de dar el respaldo —dos declaraciones seguidas— no existe
         * en `sx`: allí un array son puntos de ruptura, así que
         * `["100vh", "100dvh"]` no era un respaldo, era «100vh en móvil y
         * 100dvh a partir de tableta», justo al revés de lo que hacía falta.
         */
        ":root": {
          "--alto-pantalla": "100vh",
          "--ancho-pantalla": "100vw",
          ...variablesClaras,
          // Le dice al navegador de qué color son sus propios adornos: los
          // campos de formulario nativos, los menús desplegables del sistema
          // y el fondo que asoma al rebotar el desplazamiento.
          colorScheme: "light",
        },
        "html.dark": {
          ...variablesOscuras,
          colorScheme: "dark",
        },
        "@supports (height: 100dvh)": {
          ":root": {
            "--alto-pantalla": "100dvh",
            "--ancho-pantalla": "100dvw",
          },
        },
        "html, body, #root": { height: "100%" },
        html: {
          // Safari infla el texto al girar el móvil si no se le dice que no.
          WebkitTextSizeAdjust: "100%",
        },
        body: {
          backgroundColor: brand.canvas,
          WebkitFontSmoothing: "antialiased",
          // Sin esto el doble toque amplía la página y cada pulsación arrastra
          // un retardo de 300 ms: los dos delatan que no es una app.
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
          // Corta el rebote elástico y el «tirar para recargar» del navegador.
          overscrollBehaviorY: "none",
        },
        "*:focus-visible": {
          outline: `2px solid ${brand.violet}`,
          outlineOffset: 2,
          borderRadius: "6px",
        },

        // --- Superficies que pinta el navegador ---------------------------
        // La selección, el cursor, la barra de desplazamiento y el subrayado
        // de los enlaces vienen con los colores del sistema y no pertenecen a
        // ninguna paleta. Son de lo poco que delata que una pantalla se ha
        // montado en vez de diseñarse.
        "::selection": {
          backgroundColor: velo(brand.violet, 18),
          color: brand.ink,
        },
        "input, textarea": { caretColor: brand.violet },
        a: { textUnderlineOffset: "0.18em" },
        // Números que no bailan al actualizarse: importes, referencias y
        // contadores comparten ancho de cifra.
        "table, [data-numeric]": { fontVariantNumeric: "tabular-nums" },
        "*": {
          scrollbarColor: `${brand.line} transparent`,
          scrollbarWidth: "thin",
        },
        "::-webkit-scrollbar": { width: 10, height: 10 },
        "::-webkit-scrollbar-track": { background: "transparent" },
        "::-webkit-scrollbar-thumb": {
          backgroundColor: brand.line,
          borderRadius: 99,
          // El borde transparente adelgaza el pulgar sin encoger la zona en
          // la que se puede agarrar con el ratón.
          border: "3px solid transparent",
          backgroundClip: "content-box",
        },
        "@media (hover: hover)": {
          "::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "var(--marca-barra-encima)",
          },
        },

        // Quien pide menos movimiento no pide ninguno.
        //
        // Se corta lo que se repite o recorre distancia —los fotogramas, que
        // es lo que marea— y se dejan vivas las transiciones de color y
        // opacidad, que son las que explican qué acaba de cambiar. Apagarlo
        // todo deja una interfaz que no confirma nada, y eso es peor.
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": {
            animationDuration: "1ms !important",
            animationIterationCount: "1 !important",
          },
          "html:focus-within": { scrollBehavior: "auto" },
        },
        /*
          Barrido del cambio de tema.

          Durante una transición de vista el navegador superpone una foto de
          antes y una de después, y de fábrica las cruza en un fundido. Aquí
          se les quita esa animación —la de después la lleva `barrido.js`, que
          la recorta en un círculo creciente— y se ordenan: la nueva encima de
          la vieja. Con el fundido puesto, el círculo se vería translúcido.
        */
        "::view-transition-old(root), ::view-transition-new(root)": {
          animation: "none",
          mixBlendMode: "normal",
        },
        "::view-transition-old(root)": { zIndex: 1 },
        "::view-transition-new(root)": { zIndex: 2 },

        // Campos sueltos sin envoltorio de MUI (los de MUI se ajustan en
        // `MuiInputBase`, que gana en especificidad a este selector).
        "@media (pointer: coarse)": {
          "input, textarea, select": { fontSize: "16px" },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none" },
        outlined: { borderColor: brand.line },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0, variant: "outlined" },
      styleOverrides: {
        root: {
          borderColor: brand.line,
          boxShadow: softShadow,
          overflow: "visible",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingInline: 18,
          minHeight: 44,
          fontSize: "0.875rem",
          fontWeight: 700,
          // Una etiqueta partida en dos líneas parte también la altura del
          // botón y desalinea la fila entera.
          whiteSpace: "nowrap",
          ...pressFeedback,
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${brand.violet}, ${brand.violetDeep})`,
          "&:hover": {
            background: `linear-gradient(135deg, ${brand.violetDeep}, ${brand.violet})`,
          },
        },
        outlined: { borderColor: brand.line, color: brand.ink },
        sizeSmall: {
          minHeight: 36,
          paddingInline: 12,
          // Un botón pequeño sigue siendo un botón: con el dedo necesita los
          // mismos 44 px que los demás aunque se dibuje más bajo.
          "@media (pointer: coarse)": { minHeight: 44 },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          // Con el dedo, 36 px se falla; 44 es el mínimo cómodo. Solo en
          // táctil: con ratón un botón de 44 px se ve desproporcionado.
          "@media (pointer: coarse)": { minWidth: 44, minHeight: 44 },
          ...pressFeedback,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 8 },
        sizeSmall: { height: 24 },
        // Solo los que se pueden pulsar: un chip de estado no es un control y
        // encogerlo al tocarlo prometería algo que no ocurre.
        clickable: pressFeedback,
      },
    },
    MuiListItemButton: {
      styleOverrides: { root: pressFeedback },
    },
    MuiCardActionArea: {
      styleOverrides: { root: pressFeedback },
    },
    MuiBottomNavigationAction: {
      styleOverrides: { root: pressFeedback },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          // La casilla se dibuja pequeña, pero con el dedo hay que poder
          // fallar: la zona sensible crece sin que cambie el aspecto. El
          // icono de la variante pequeña mide 20 px, así que 12 de acolchado
          // a cada lado dan justo los 44 del mínimo cómodo.
          "@media (pointer: coarse)": { padding: 12 },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textUnderlineOffset: "0.18em",
          transition: `color ${timing.pulsacion}ms ease`,
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small", fullWidth: true },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          // Safari en iOS amplía la página al enfocar un campo cuyo texto mide
          // menos de 16 px, y no hay manera de desactivarlo desde el viewport:
          // la única salida es que en táctil los campos midan justo 16. Se
          // decide por tipo de puntero y no por ancho, porque lo que importa
          // es que se toque con el dedo, no que la pantalla sea estrecha.
          "@media (pointer: coarse)": { fontSize: "16px" },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: brand.papel,
          "& fieldset": { borderColor: brand.line },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: "0.9rem" } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: brand.line },
        head: {
          fontWeight: 600,
          fontSize: "0.78rem",
          color: brand.muted,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          backgroundColor: velo(brand.canvas, 70),
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        // El primer aviso se hace esperar, para que no salte por rozarlo sin
        // querer. Pero una vez hay uno abierto, los de al lado aparecen casi
        // al momento: recorrer una fila de iconos deja de dar tirones y toda
        // la barra parece más rápida.
        enterDelay: 420,
        enterNextDelay: 90,
        leaveDelay: 0,
        enterTouchDelay: 500,
        slotProps: {
          transition: { timeout: { enter: timing.globo, exit: 100 } },
        },
      },
      styleOverrides: {
        tooltip: {
          // De noche el globo se invierte igual que de día: claro sobre
          // oscuro pasa a oscuro sobre claro. Con `ink` a secas se habría
          // quedado con fondo claro y letra blanca, ilegible.
          backgroundColor: "var(--marca-globo)",
          color: "var(--marca-globo-texto)",
          fontSize: "0.75rem",
          fontWeight: 500,
          borderRadius: 8,
          paddingBlock: 6,
          paddingInline: 10,
        },
        arrow: { color: "var(--marca-globo)" },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 18, boxShadow: raisedShadow },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, alignItems: "flex-start" },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 8, borderRadius: 99, backgroundColor: brand.line },
        bar: { borderRadius: 99 },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: { "&.Mui-completed": { color: brand.green } },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, minHeight: 46 },
      },
    },
  },
});

/** Tonos de estado compartidos por chips, badges y avisos. */
export const toneStyles = {
  green: { color: brand.green, bg: brand.greenBg },
  amber: { color: brand.amber, bg: brand.amberBg },
  blue: { color: brand.violet, bg: brand.violetBg },
  red: { color: brand.red, bg: brand.redBg },
  slate: { color: brand.muted, bg: velo(brand.muted, 12) },
};
